import * as signalR from '@microsoft/signalr';
import { ServicesBuilder } from '../utils/setup';
import { getPlatforms, isPlatform } from '@ionic/vue';
import { eventBus } from '@/libs/EventBus';
import { WorkManager } from './work.manager';
import { logError, logInfo, serializeError } from '@/utils/logger';
import { captureException } from '@sentry/vue';
import {
    type IServiceOptions,
    type IPushData,
    type IInvoice,
    type IShift,
    MessageType,
    AppConfig,
    IPrintJob,
    PrintJobStatus,
    InvoiceAction,
    InvoiceStatus,
    ICustomer,
    PrinterType,
    PrinterConnections,
    ShiftStatus,
} from '@posx/core';
import { useInvoiceStore } from '@/stores/invoice';
import { useSectionStore } from '@/stores/section';
import { useConfigStore } from '@/stores/config';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { usePrinterStore } from '@/stores/printer';
import { usePaymentStore } from '@/stores/payment';
import { getPlatform } from '@/utils/misc';
import { cleanupOnShiftClose } from '@/utils/cleanup';
import { markConnectionLost, reportDeviceState } from '@/utils/power.diagnostics';

export class DataWorker {
    private static instance: DataWorker;
    private static connection: signalR.HubConnection;
    private static options: IServiceOptions;
    private static url: string;
    private static connectionLock: Promise<signalR.HubConnection | undefined> | null = null;
    private static retryTimeout: ReturnType<typeof setTimeout> | null = null;
    private static retryCount = 0;
    private static shuttingDown = false; // Prevents retry scheduling during closeConnection
    private static onlineHandler: (() => void) | null = null;
    // Sync ends race fix: defer emit until in-flight saves complete + settle period
    private static syncInFlight = 0;
    private static pendingEnds: 'sync ends' | 'sync sensitive data ends' | null = null;
    private static settleTimer: ReturnType<typeof setTimeout> | null = null;
    private static readonly SETTLE_MS = 300;
    private static deferredUid = ''; // last send_order deferred for a busy table, logs once instead of every ~1s redelivery

    private constructor() {}

    private static onSyncActivity() {
        if (!DataWorker.pendingEnds) return;
        if (DataWorker.settleTimer) clearTimeout(DataWorker.settleTimer);
        DataWorker.settleTimer = setTimeout(() => DataWorker.flushEnds(), DataWorker.SETTLE_MS);
    }

    private static flushEnds() {
        if (!DataWorker.pendingEnds || DataWorker.syncInFlight > 0) return;
        const ev = DataWorker.pendingEnds;
        DataWorker.pendingEnds = null;
        eventBus.emit(ev, ev);
    }

    public static getInstance(): DataWorker {
        if (!DataWorker.instance) {
            DataWorker.instance = new DataWorker();
        }
        return DataWorker.instance;
    }

    public async createOrUpdateConnection(options: IServiceOptions): Promise<signalR.HubConnection | undefined> {
        if (DataWorker.connectionLock) return DataWorker.connectionLock;
        return DataWorker.connectionLock = this.doCreateOrUpdateConnection(options).finally(() => DataWorker.connectionLock = null);
    }

    private async doCreateOrUpdateConnection(options: IServiceOptions): Promise<signalR.HubConnection | undefined> {
        DataWorker.shuttingDown = false;
        DataWorker.options = options;
        if (options.merchant_uid) {
            try {
                logInfo('getPlatforms', getPlatforms());
                const configStore = useConfigStore();
                const receiptConfig = configStore.appConfig?.config?.receipt as { printer_worker_interval?: number } | undefined;
                const printerWorkerInterval = receiptConfig?.printer_worker_interval ?? 300;
                if (getPlatform() == 'ios' || getPlatform() == 'android' || getPlatform() == 'electron')
                    WorkManager.getInstance().startPrinterWorker(printerWorkerInterval);
                if(getPlatform() == 'ios' || getPlatform() == 'android' || getPlatform() == 'electron' || getPlatform() == 'web')
                    WorkManager.getInstance().startNetworkWorker(300000); // 5 minutes

                const newUrl = `${options.base_url}xhub/?merchant-uid=${options.merchant_uid}&device-uid=${options.device_uid}&token=${options.token}`;
                // URL unchanged and connection active/reconnecting, reuse existing connection
                const isActiveConnection = DataWorker.connection?.state === signalR.HubConnectionState.Connected ||
                    DataWorker.connection?.state === signalR.HubConnectionState.Reconnecting ||
                    DataWorker.connection?.state === signalR.HubConnectionState.Connecting;
                if (DataWorker.connection && isActiveConnection && DataWorker.url === newUrl)
                    return DataWorker.connection;
                // Destroy old connection before creating new one
                if (DataWorker.connection) {
                    try { await DataWorker.connection.stop(); } catch {} // Ignore stop errors
                }
                // Always create fresh connection to ensure event handlers bind only once
                DataWorker.url = newUrl;
                DataWorker.connection = new signalR.HubConnectionBuilder()
                    .withUrl(DataWorker.url, { withCredentials: false })
                    .configureLogging(signalR.LogLevel.Information)
                    .withAutomaticReconnect({ nextRetryDelayInMilliseconds: () => 3000 })
                    .build();
                // Match server ClientTimeoutInterval=45s (3x keepAlive=15s)
                DataWorker.connection.serverTimeoutInMilliseconds = 45000;
                DataWorker.connection.keepAliveIntervalInMilliseconds = 15000;

                DataWorker.connection.on('sync', async (syncData: IPushData) => {
                    try {
                        if (syncData) {
                            if (syncData.type === MessageType.Event) {
                                if (syncData.data === 'sync ends' || syncData.data === 'sync sensitive data ends') {
                                    DataWorker.pendingEnds = syncData.data;
                                    DataWorker.onSyncActivity();
                                    return 1;
                                } else if (syncData.data === 'node detected') {
                                    console.log('Node detected');
                                    return 1;
                                }
                            } else if (syncData.type === MessageType.Data) {
                                // if print job status is printed, then just return 1 and don't save it to the database
                                if (syncData.model === 'PrintJob') {
                                    const printJob = JSON.parse(syncData.data) as IPrintJob;
                                    if (
                                        (printJob && printJob.status === PrintJobStatus.Printed)
                                        || (printJob && printJob.connection_type === PrinterConnections.IMIN_BUILT_IN)
                                        || (printJob && printJob.connection_type === PrinterConnections.WINDOWS_DRIVER_BUILT_IN)
                                        || secureStorage.getCachedItem('is_main_device') != 'true'
                                    )
                                        return 1;
                                }
                                if (syncData.model === 'Shift') {
                                    const shift = JSON.parse(syncData.data) as IShift;
                                    // Only cleanup for recent shift close (within 24h) to skip historical sync
                                    if (shift.status === ShiftStatus.Closed && shift.ended_at && Date.now() - new Date(shift.ended_at).getTime() < 24 * 60 * 60 * 1000)
                                        cleanupOnShiftClose().catch(e => logError('cleanupOnShiftClose failed', { error: serializeError(e) }));
                                }
                                let service;
                                try { service = await ServicesBuilder.getInstance().getServiceByName(syncData.model); }
                                catch (error) {
                                    if (String((error as Error)?.message ?? '').includes('No service found for model')) { logInfo('sync: skip unsupported model', { model: syncData.model }); return 1; }
                                    throw error;
                                }
                                DataWorker.syncInFlight++;
                                DataWorker.onSyncActivity();
                                try {
                                    const data = JSON.parse(syncData.data);
                                    if (!syncData.has_many) await service.saveOne(data);
                                    else await service.saveMany(data);
                                } finally {
                                    DataWorker.syncInFlight--;
                                    DataWorker.onSyncActivity();
                                }
                            }
                        }
                        return 1;
                    } catch (error) {
                        const err = error as Error;
                        const isQuotaError = err.name === 'QuotaExceededError'
                            || (err.name === 'AbortError' && (err.message ?? '').includes('QuotaExceeded'));
                        if (isQuotaError) {
                            logError('sync: storage quota exceeded', { model: syncData.model });
                        } else {
                            // IDB aborted/timed out while the app was suspended (XPOS-1Y5/234): server re-sends until we return 1, so log only
                            if (!/Transaction aborted|TimeoutError/.test(`${err.name} ${err.message}`)) captureException(error);
                            logError('sync: error saving data', { error: serializeError(error), model: syncData.model });
                        }
                        return 0;
                    }
                });

                DataWorker.connection.on('send_order', async (syncData: IPushData) => {
                    try {
                        const invoice = JSON.parse(syncData.data) as IInvoice;
                        logInfo('send_order: processing', { uid: invoice.uid, table_uid: invoice.table_uid, action: invoice.action });
                        // Validate required keys before database operations
                        // poison: missing uid = malformed message, ack to drop
                        if (!invoice.uid) {
                            logError('send_order: missing invoice uid');
                            return 1;
                        }
                        // Skip table_uid validation for online delivery orders
                        const isOnlineDeliveryOrder = invoice.action === InvoiceAction.SettleOnlineDeliveryOrder;
                        // poison: non-delivery order without table_uid = malformed
                        if (!isOnlineDeliveryOrder && !invoice.table_uid) {
                            logError('send_order: missing table_uid');
                            return 1;
                        }

                        const invoiceStore = useInvoiceStore();
                        const sectionStore = useSectionStore();
                        const sectionItemService = await sectionStore.getSectionItemService(true);
                        const invoiceService = await invoiceStore.getService(true);
                        const sectionItem = isOnlineDeliveryOrder
                            ? null
                            : await sectionItemService.getOne(invoice.table_uid);
                        const configStore = useConfigStore();
                        if (invoice.customer_id) {
                            const customer = await invoiceStore.getCustomer(invoice.customer_id);
                            if (customer) invoice.customer = customer;
                        }
                        // transient: config not loaded yet (SignalR ready before liveQuery), request redelivery
                        if (!configStore.appConfig) {
                            logInfo('send_order: config not loaded, requesting redelivery');
                            return 0;
                        }
                        switch (invoice.action) {
                            case InvoiceAction.PayAtCounter:
                                if (!invoice.ref_id) invoice.ref_id = await invoiceService.getRefId();
                                if (!invoice.call_num) invoice.call_num = invoice.ref_id.slice(-3);
                                invoice.status = InvoiceStatus.PayAtCounter;
                                await invoiceService.addInvoice(invoice);
                                eventBus.emit('qr_order_received', { uid: invoice.uid, call_num: invoice.call_num });
                                return 1;

                            case InvoiceAction.SettleOnlineDeliveryOrder:
                            case InvoiceAction.PayInvoice:
                                const printerStore = usePrinterStore();
                                const paymentStore = usePaymentStore();
                                const paymentMethodService = await paymentStore.getPaymentMethodService(true);
                                const paymentService = await paymentStore.getPaymentService(true);
                                let paymentMethodCode = configStore.appConfig?.config?.online_order?.paynow_method ?? 'WPPN';
                                if (invoice.action === InvoiceAction.SettleOnlineDeliveryOrder)
                                    paymentMethodCode = invoice.meta?.delivery_payment_method_code ?? paymentMethodCode;
                                else if (invoice.meta?.payment_method_code)
                                    paymentMethodCode = invoice.meta.payment_method_code;
                                const paymentMethod = await paymentMethodService.getOneByParams({
                                    payment_method_code: paymentMethodCode,
                                });
                                await paymentService.addPayment(invoice, invoice.grand_total, paymentMethod!);
                                const terminalPrinter = await printerStore.getTerminalPrinter(PrinterType.ReceiptPrinter);
                                const receiptSaveOnly = configStore.appConfig?.config?.online_order?.disable_qr_order_receipt ?? false;
                                await invoiceService.directPayInvoice(invoice, undefined, undefined, terminalPrinter || undefined, undefined, false, receiptSaveOnly);
                                eventBus.emit('qr_order_received', { uid: invoice.uid, call_num: invoice.call_num });
                                return 1;

                            default:
                                // poison: sectionItem not found = table doesn't exist locally, ack to drop
                                if (!sectionItem) {
                                    logError('send_order: section item not found', { table_uid: invoice.table_uid });
                                    return 1;
                                }
                                // transient: table open here with unsaved draft or in-flight payment, takeover would misroute the payment (#1703)
                                if (sectionStore.currentSectionItem?.uid === invoice.table_uid && (invoiceStore.paymentInProgress || (invoiceStore.currentInvoice?.lines?.length && !invoiceStore.currentInvoice.id_in_server))) {
                                    if (DataWorker.deferredUid !== invoice.uid) logInfo('send_order: table busy on this device, requesting redelivery', { uid: invoice.uid, table_uid: invoice.table_uid });
                                    DataWorker.deferredUid = invoice.uid;
                                    return 0;
                                }
                                let invoiceFromDb = await invoiceService.getOne(sectionItem.invoice_uid);
                                // Wait up to 15s for invoice to get id_in_server from sync
                                if (sectionItem.invoice_uid && invoiceFromDb && !invoiceFromDb.id_in_server)
                                    for (let i = 0; i < 50 && !invoiceFromDb?.id_in_server; i++) {
                                        await new Promise((resolve) => setTimeout(resolve, 300));
                                        invoiceFromDb = await invoiceService.getOne(sectionItem.invoice_uid);
                                    }
                                sectionItem.invoice_created_at = invoiceFromDb?.created_at ?? new Date();
                                if (invoiceFromDb) {
                                    const mergedInvoice = invoiceService.appendInvoice(invoiceFromDb, invoice);
                                    invoiceStore.calculateTotalItemQuantity(mergedInvoice);
                                    invoiceService.calculate(mergedInvoice, configStore.appConfig, true);
                                    await invoiceService.updateInvoice(mergedInvoice, sectionItem);
                                } else {
                                    // Lock sectionItem first to prevent concurrent duplicate order creation
                                    sectionItem.invoice_uid = invoice.uid;
                                    await sectionItemService.saveOne(sectionItem);
                                    invoiceStore.setInvoiceType(invoice, sectionItem);
                                    invoiceStore.calculateTotalItemQuantity(invoice);
                                    invoiceService.calculate(invoice, configStore.appConfig, true);
                                    await invoiceService.createInvoice(invoice, sectionItem);
                                }
                                eventBus.emit('qr_order_received', { uid: invoice.uid, table_label: sectionItem.name });
                                return 1;
                        }
                    } catch (error) {
                        // transient: HTTP timeout, network error, etc. → request redelivery
                        captureException(error);
                        logError('send_order: error processing, requesting redelivery', { error: serializeError(error) });
                        return 0;
                    }
                });

                // Handle connection close - silent handling for expected errors, report unknown ones
                DataWorker.connection.onclose(async (error: Error | undefined) => {
                    markConnectionLost(error?.message);
                    if (error?.message.includes('Device is not registered')) {
                        eventBus.emit('device not registered', true);
                        logInfo('Device not registered');
                        return;
                    }
                    // WebSocket 1006, negotiation failure (Android OkHttp) - transient network errors, auto-retries handle them
                    if (error?.message.includes('Socket closed') || error?.message.includes('Handshake was canceled') || error?.message.includes('WebSocket closed with status code: 1006') || error?.message.includes('Cannot send data') || error?.message.includes('Failed to complete negotiation')) {
                        logInfo('SignalR connection closed', { error: error.message });
                        return;
                    }
                    if (error) captureException(error);
                    // Server Close with allowReconnect=false bypasses withAutomaticReconnect, so rebuild it; fixed 30s caps repeated server errors
                    if (!DataWorker.shuttingDown && error?.message.includes('Server returned an error on close')) this.scheduleReconnect(30000);
                });

                DataWorker.connection.onreconnecting((error) => {
                    logInfo('SignalR reconnecting', { reason: error?.message });
                    markConnectionLost(error?.message);
                    eventBus.emit('connectedChange', false);
                });
                DataWorker.connection.onreconnected(() => {
                    logInfo('SignalR reconnected');
                    reportDeviceState('reconnected');
                    eventBus.emit('connectedChange', true);
                });

                await DataWorker.connection.start();
                DataWorker.retryCount = 0; // Reset retry count on successful connection
                logInfo('SignalR connected');
                reportDeviceState('connected'); // flush offline events buffered by power diagnostics
                eventBus.emit('connectedChange', true);
                eventBus.emit('websocketInitialized', true);
                return DataWorker.connection;
            } catch (error) {
                const msg = (error as Error)?.message;
                // Handle device not registered error same as onclose handler
                if (msg?.includes('Device is not registered')) {
                    if (DataWorker.retryTimeout) { clearTimeout(DataWorker.retryTimeout); DataWorker.retryTimeout = null; }
                    if (DataWorker.onlineHandler) { window.removeEventListener('online', DataWorker.onlineHandler); DataWorker.onlineHandler = null; }
                    DataWorker.retryCount = 0;
                    eventBus.emit('device not registered', true);
                    logInfo('Device not registered');
                    return;
                }
                // Abort triggered by closeConnection: not a failure, so don't report it and don't retry
                if (DataWorker.shuttingDown) { eventBus.emit('connectedChange', false); return; }
                if (msg?.includes('Handshake was canceled') || msg?.includes('Failed to complete negotiation') || msg?.includes('stopped during negotiation') || msg?.includes('Failed to start the HttpConnection before stop() was called')) logInfo('SignalR connection interrupted, retrying...', { error: msg });
                else { captureException(error); logError('SignalR connection failed', { error: msg }); }
                eventBus.emit('connectedChange', false);
                // Exponential backoff capped at 30s
                this.scheduleReconnect(Math.min(3000 * Math.pow(2, DataWorker.retryCount++), 30000));
            }
        }
    }

    // Rebuild the connection after delay, timeout stored for cleanup in closeConnection()
    private scheduleReconnect(delay: number) {
        if (DataWorker.retryTimeout) clearTimeout(DataWorker.retryTimeout);
        DataWorker.retryTimeout = setTimeout(() => {
            if (!DataWorker.connectionLock) this.createOrUpdateConnection(DataWorker.options);
        }, delay);
        // Reconnect immediately on network recovery instead of waiting for next timer
        if (!DataWorker.onlineHandler) {
            DataWorker.onlineHandler = () => {
                DataWorker.onlineHandler = null;
                if (DataWorker.retryTimeout) { clearTimeout(DataWorker.retryTimeout); DataWorker.retryTimeout = null; }
                DataWorker.retryCount = 0;
                if (!DataWorker.connectionLock) this.createOrUpdateConnection(DataWorker.options);
            };
            window.addEventListener('online', DataWorker.onlineHandler, { once: true });
        }
    }

    public async closeConnection(): Promise<void> {
        DataWorker.shuttingDown = true;
        if (DataWorker.retryTimeout) { clearTimeout(DataWorker.retryTimeout); DataWorker.retryTimeout = null; }
        if (DataWorker.settleTimer) { clearTimeout(DataWorker.settleTimer); DataWorker.settleTimer = null; }
        if (DataWorker.onlineHandler) { window.removeEventListener('online', DataWorker.onlineHandler); DataWorker.onlineHandler = null; }
        DataWorker.retryCount = 0;
        DataWorker.syncInFlight = 0;
        DataWorker.pendingEnds = null;
        WorkManager.getInstance().stopPrinterWorker(); // Stop timer workers to prevent memory leak on logout
        WorkManager.getInstance().stopNetworkWorker();
        if (DataWorker.connection) await DataWorker.connection.stop();
    }

    public getConnection(): signalR.HubConnection {
        return DataWorker.connection;
    }
}
